import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';

@Component({
  selector: 'app-input-dropdown',
  template: `
    <mat-form-field>
      <mat-label *ngIf="controlLabel">{{controlLabel}}</mat-label>
      <input [formControl]="inputControl"
             [matAutocomplete]="auto"
             matInput
             #inputTag
             name="{{elementId}}"
             placeholder="{{placeholder}}">
      <mat-autocomplete (optionSelected)="valueChanged($event)" #auto="matAutocomplete"
                        [displayWith]="createDisplayFn">
        <mat-option *ngFor="let element of filteredData | async | orderBy: orderByValue"
                    [value]="element.id"
                    data-cy="input-dropdown-option"
                    [ngStyle]="{paddingLeft: element.level? element.level*17 + 'px' : '5px'}">
          {{includeIdInOptionView ? element.id + ' - ' : ''}}{{element.name}}
        </mat-option>
      </mat-autocomplete>
      <mat-error *ngIf="inputControl.hasError('required')">
        Missing {{label}}
      </mat-error>
    </mat-form-field>
  `,
  styleUrls: ['dropdown.component.scss']
})
export class DropdownComponent<T> implements OnInit, OnChanges {
  @Input() elementId!: string;
  @Input() placeholder = 'Type to search';
  @Input() controlLabel?: string;
  @Input() data!: any[];
  @Input() orderByValue!: string;
  @Input() label!: string;
  @Input() maxNumberOfItemsToShow = 100;

  @Input() includeIdInOptionView = true;

  @Output() valueChange: EventEmitter<T> = new EventEmitter();

  @Input() inputControl!: FormControl;
  filteredData!: Observable<any>;

  createDisplayFn = (id: number): string => {
    if (id) {
      const selectedData = this.data.find((element) => {
        return element.id === id;
      });
      if (selectedData) {
        if (this.includeIdInOptionView) {
          return selectedData.id + ' - ' + selectedData.name;
        }
        return selectedData.name;
      }
    }
    return '';
  };

  ngOnInit() {
    this.filteredData = this.getFilteredData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.filteredData = this.getFilteredData();
    }
  }

  getFilteredData(): Observable<T[]> {
    return this.inputControl.valueChanges
      .pipe(
        startWith(''),
        map((formInputValue) => {
          return formInputValue && typeof formInputValue === 'object' ? this.createDisplayFn(formInputValue.id) : formInputValue;
        }),
        map((val) => {
          return val ? this._filter(val).slice(0, this.maxNumberOfItemsToShow) : this.data.slice(0, this.maxNumberOfItemsToShow);
        })
      );
  }

  valueChanged(event: MatAutocompleteSelectedEvent) {
    this.valueChange.emit(this.data.find(it => it.id === event.option.value));
  }

  private _filter(val: string) {
    return this.data.filter((option) => {
      return new RegExp(val, 'gi').test(this.createDisplayFn(option.id) as string);
    });
  }
}
